import Phaser from 'phaser';

import * as ABCya from '@abcya/abcya_phaser';
import ImageKeys from '../UIAssetKeys';
import CubeButton, { CubeButtonEvents } from '../CubeButton';
import Game, { GameEvents, GameStates } from '../scenes/Game';
import { getTotalFoundAreas, getTotalFoundShapes, resetGameData } from '../gameData';
import UIAssetKeys from '../UIAssetKeys';
import SoundAssetKeys from '../SoundAssetKeys';

export default class Title extends Phaser.GameObjects.Container
{
    /**
     * Create the Title screen.
     * @param {Game} scene the owner scene
     */
    constructor(scene)
    {
        super(scene);

        this.scene = scene;

        this.scene.add.existing(this);

        this._addLetters();
        this._createLetsGoButton();
        this._createNewGameConfirmation();
        this._createNewGameContinuePopups();

        //this._addReference();
        //ABCya.Debug.enableImagePlacerDebug(this.scene);

        this._createSoundObjects();

        this.reset();
    }

    /**
     * Reset should be called when you want to set the state to a disabled/inactive state.
     */
    reset()
    {
        // send everything off screen and set alpha to 0
        this._sendTargetsOffScreen();
        this.setAlpha(0);
        this.letsGoButton.disableInteractive();
        this.newGameButton.disableInteractive();
        this.yesButton.disableInteractive();
        this.noButton.disableInteractive();
        this.continueButton.disableInteractive();
    }

    /**
     * Prepare should be called before you want to prepare the state to become active.
     */
    prepare()
    {
        this.setAlpha(1);
        this._sendTargetsOffScreen();
        this.letsGoButton.disableInteractive();
        this.newGameButton.disableInteractive();
        this.yesButton.disableInteractive();
        this.noButton.disableInteractive();
        this.continueButton.disableInteractive();
    }

    /**
     * Animates the states in.
     */
    async begin()
    {
        let tweens = [
            { targets: this.letterS, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.letterS.onScreenPosition.y } },
            { targets: this.letterH, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.letterH.onScreenPosition.y }, offset: 100 },
            { targets: this.letterA, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.letterA.onScreenPosition.y }, offset: 200 },
            { targets: this.letterP, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.letterP.onScreenPosition.y }, offset: 300 },
            { targets: this.letterE2, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.letterE2.onScreenPosition.y }, offset: 400 },
            { targets: this.letterG, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.letterG.onScreenPosition.y }, offset: 200 },
            { targets: this.letterE1, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.letterE1.onScreenPosition.y }, offset: 300 },
            { targets: this.letterT, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.letterT.onScreenPosition.y }, offset: 400 },
            { targets: this.letterIn, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.letterIn.onScreenPosition.y }, offset: 500 },
            { targets: this.letsGoButton, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { scale: 1 }, offset: 600 },
        ];

        if (this.jingleAudio.isPlaying && this.jingleAudio.volume != 1)
        {
            tweens.push({ targets: this.jingleAudio, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { volume: 1 }, offset: 500 });
        }

        ABCya.Audio.playOneShot(SoundAssetKeys._title_drop_in, this.scene, 1, false, 0.25);
        ABCya.Audio.playOneShot(SoundAssetKeys._get_in_shape, this.scene, 1, false, 0.75);

        let timeline = this.scene.tweens.timeline({
            tweens: tweens,
            onComplete: () =>
            {
                this.letsGoButton.setInteractive();
                if (!this.jingleAudio.isPlaying)
                {
                    ABCya.Audio.playSound(SoundAssetKeys._title_jingle, this.jingleAudio, 1, true);
                }
            },
        });

        let waitForTimeline = new Promise((resolve) =>
        {
            timeline.on(Phaser.Tweens.Events.TIMELINE_COMPLETE, () => resolve());
        })
        await waitForTimeline;
    }

    /**
     * Animates the state out.
     */
    async leave()
    {
        this.letsGoButton.disableInteractive();
        this.newGameButton.disableInteractive();
        this.yesButton.disableInteractive();
        this.noButton.disableInteractive();
        this.continueButton.disableInteractive();

        let tweens = [
            { targets: this.jingleAudio, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { volume: 1 }, offset: 0 },
            { targets: this.letterS, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { y: this.letterS.offScreenPosition.y }, offset: 500 },
            { targets: this.letterH, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { y: this.letterH.offScreenPosition.y }, offset: 400 },
            { targets: this.letterA, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { y: this.letterA.offScreenPosition.y }, offset: 300 },
            { targets: this.letterP, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { y: this.letterP.offScreenPosition.y }, offset: 200 },
            { targets: this.letterE2, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { y: this.letterE2.offScreenPosition.y }, offset: 100 },
            { targets: this.letterG, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { y: this.letterG.offScreenPosition.y }, offset: 300 },
            { targets: this.letterE1, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { y: this.letterE1.offScreenPosition.y }, offset: 200 },
            { targets: this.letterT, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { y: this.letterT.offScreenPosition.y }, offset: 100 },
            { targets: this.letterIn, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { y: this.letterIn.offScreenPosition.y }, offset: 0 },
            { targets: this.letsGoButton, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { scale: 0 }, offset: 0 },
            { targets: this.newGameConfirmation, duration: 750, ease: Phaser.Math.Easing.Quadratic.In, props: { alpha: 0 }, offset: 0 },
            { targets: this.newGameContinue, duration: 750, ease: Phaser.Math.Easing.Quadratic.In, props: { alpha: 0 }, offset: 0 },
        ];

        ABCya.Audio.playOneShot(SoundAssetKeys._title_jump_up, this.scene);

        let timeline = this.scene.tweens.timeline({
            tweens: tweens,
        });

        let waitForTimeline = new Promise((resolve) =>
        {
            timeline.on(Phaser.Tweens.Events.TIMELINE_COMPLETE, () => resolve());
        })
        await waitForTimeline;
    }

    /**
     * Add a reference image to this state.
     * @param {Boolean} enablePlacer whether to enable the image placer debug tool after adding the reference image
     */
    _addReference(enablePlacer = true)
    {
        ABCya.Debug.addScreenReference(this.scene, "TitleReference", "assets/reference_images/1 Get in Shape_Title-01.jpg", (image) =>
        {
            if (enablePlacer) ABCya.Debug.enableImagePlacerDebug(this.scene, this.scene.background, image);
        });
    }

    _createSoundObjects()
    {
        this.jingleAudio = ABCya.Audio.createSound(this.scene, 1, true, 0);
    }

    /**
     * Adds the letter graphics.
     */
    _addLetters()
    {
        this.letterG = ABCya.Images.addImage(219, 190, ImageKeys._01_Title_Screen_Title_G, this);
        this._setAnimationPositions(this.letterG, { x: this.letterG.x, y: -190 });

        this.letterE1 = ABCya.Images.addImage(417, 191, ImageKeys._01_Title_Screen_Title_E, this);
        this._setAnimationPositions(this.letterE1, { x: this.letterE1.x, y: -190 });

        this.letterT = ABCya.Images.addImage(617, 180, ImageKeys._01_Title_Screen_Title_T, this);
        this._setAnimationPositions(this.letterT, { x: this.letterT.x, y: -190 });

        this.letterIn = ABCya.Images.addImage(781, 228, ImageKeys._01_Title_Screen_Title_IN, this);
        this._setAnimationPositions(this.letterIn, { x: this.letterIn.x, y: -190 });

        this.letterS = ABCya.Images.addImage(122, 402, ImageKeys._01_Title_Screen_Title_S, this);
        this._setAnimationPositions(this.letterS, { x: this.letterS.x, y: -190 });

        this.letterH = ABCya.Images.addImage(317, 402, ImageKeys._01_Title_Screen_Title_H, this);
        this._setAnimationPositions(this.letterH, { x: this.letterH.x, y: -190 });

        this.letterA = ABCya.Images.addImage(513, 402, ImageKeys._01_Title_Screen_Title_A, this);
        this._setAnimationPositions(this.letterA, { x: this.letterA.x, y: -190 });

        this.letterP = ABCya.Images.addImage(707, 381, ImageKeys._01_Title_Screen_Title_P, this);
        this._setAnimationPositions(this.letterP, { x: this.letterP.x, y: -190 });

        this.letterE2 = ABCya.Images.addImage(901, 402, ImageKeys._01_Title_Screen_Title_E, this);
        this._setAnimationPositions(this.letterE2, { x: this.letterE2.x, y: -190 });
    }

    /**
     * Set the on/off screen positions. These are the start/end positions when the game object is animating in.
     * @param {Phaser.GameObjects.GameObject} target the game object to add the animation positions to
     * @param {{x:number, y:number}} offScreenPosition the offscreen position (the position the game object starts at when animating in)
     */
    _setAnimationPositions(target, offScreenPosition)
    {
        target.onScreenPosition = { x: target.x, y: target.y };
        target.offScreenPosition = offScreenPosition;
    }

    /**
     * Send all game objects to their start positions. Some game objects aren't sent "offscreen" but instead set to scale of 0 or alpha of 0.
     */
    _sendTargetsOffScreen()
    {
        this.letterG.setPosition(this.letterG.offScreenPosition.x, this.letterG.offScreenPosition.y);
        this.letterE1.setPosition(this.letterE1.offScreenPosition.x, this.letterE1.offScreenPosition.y);
        this.letterT.setPosition(this.letterT.offScreenPosition.x, this.letterT.offScreenPosition.y);
        this.letterIn.setPosition(this.letterIn.offScreenPosition.x, this.letterIn.offScreenPosition.y);
        this.letterS.setPosition(this.letterS.offScreenPosition.x, this.letterS.offScreenPosition.y);
        this.letterH.setPosition(this.letterH.offScreenPosition.x, this.letterH.offScreenPosition.y);
        this.letterA.setPosition(this.letterA.offScreenPosition.x, this.letterA.offScreenPosition.y);
        this.letterP.setPosition(this.letterP.offScreenPosition.x, this.letterP.offScreenPosition.y);
        this.letterE2.setPosition(this.letterE2.offScreenPosition.x, this.letterE2.offScreenPosition.y);

        this.letsGoButton.setScale(0);
        this.newGameConfirmation.setAlpha(0);
        this.newGameContinue.setAlpha(0);
    }

    /**
     * Create the Let's Go button and add its listener.
     */
    _createLetsGoButton()
    {
        this.letsGoButton = new CubeButton(this.scene, ImageKeys._01_Title_Screen_PlayBtn_Top, ImageKeys._01_Title_Screen_PlayBtn_Lft, ImageKeys._01_Title_Screen_PlayBtn_Rt);
        this.add(this.letsGoButton);
        this.letsGoButton.setPosition(512, 633);

        this.letsGoButton.on(CubeButtonEvents.CLICKED, () => 
        {
            if (getTotalFoundAreas() + getTotalFoundShapes() > 0)
            {
                // at least 1 area/shape was found, show the new game button so you can start over
                ABCya.Tweens.addTween(this.newGameContinue, 500, { alpha: 1 }, Phaser.Math.Easing.Quadratic.Out, () =>
                {
                    this.continueButton.setInteractive();
                    this.newGameButton.setInteractive();
                });
            }
            else
            {
                // just go right into the game
                this.scene.events.emit(GameEvents.GO_TO_STATE, GameStates.SHAPE_REVIEW);
            }
        });
    }

    /**
     * Create the new game confirmation popup window/button and add needed listeners.
     */
    _createNewGameConfirmation()
    {
        this.add(this.newGameConfirmation = this.scene.add.container());
        this.newGameConfirmation.add(this.scene.add.rectangle((this.scene.scale.width / 2), (this.scene.scale.height / 2), this.scene.scale.width, this.scene.scale.height, 0x306379, 0.5).setInteractive());
        ABCya.Images.addImage((this.scene.scale.width / 2), (this.scene.scale.height / 2), UIAssetKeys._Buttons_New_Game_PopUp_NewGame, this.newGameConfirmation);

        this.yesButton = new CubeButton(this.scene, ImageKeys._Buttons_Menu_Pop_Up_Btn_SndOn3, ImageKeys._Buttons_New_Game_Btn_Yes2, ImageKeys._Buttons_New_Game_Btn_Yes1);
        this.yesButton.setPosition(440, 445);
        this.yesButton.on(CubeButtonEvents.CLICKED, () => 
        {
            this.yesButton.disableInteractive();
            this.noButton.disableInteractive();

            resetGameData();
            this.scene.downtownRegion.isLocked = true;
            this.scene.campRegion.isLocked = true;
            this.scene.spaceRegion.isLocked = true;
            this.scene.events.emit(GameEvents.TOGGLE_SOUND, true);
            this.scene.events.emit(GameEvents.GO_TO_STATE, GameStates.SHAPE_REVIEW);
        });

        this.noButton = new CubeButton(this.scene, ImageKeys._Buttons_Menu_Pop_Up_Btn_SndOn3, ImageKeys._Buttons_New_Game_Btn_No2, ImageKeys._Buttons_New_Game_Btn_No1);
        this.noButton.setPosition(584, 445);
        this.noButton.on(CubeButtonEvents.CLICKED, () => 
        {
            this.yesButton.disableInteractive();
            this.noButton.disableInteractive();
            ABCya.Tweens.addTween(this.newGameConfirmation, 500, { alpha: 0 }, Phaser.Math.Easing.Quadratic.In);
        });

        this.newGameConfirmation.add([this.yesButton, this.noButton]);
    }

    /**
     * Create the new game and continue popup (which are two separate looking popups). Add needed listeners.
     */
    _createNewGameContinuePopups()
    {
        this.add(this.newGameContinue = this.scene.add.container());
        this.newGameContinue.add(this.scene.add.rectangle((this.scene.scale.width / 2), (this.scene.scale.height / 2), this.scene.scale.width, this.scene.scale.height, 0x306379, 0.5).setInteractive());

        ABCya.Images.addImage((this.scene.scale.width / 2) + 175, (this.scene.scale.height / 2), UIAssetKeys._Pop_ups_PopUp_Cont, this.newGameContinue);
        ABCya.Images.addImage((this.scene.scale.width / 2) - 175, (this.scene.scale.height / 2), UIAssetKeys._Pop_ups_PopUp_NewGm, this.newGameContinue);

        this.newGameButton = new CubeButton(this.scene, ImageKeys._Pop_ups_Btn_NwGm3, ImageKeys._Pop_ups_Btn_NwGm2, ImageKeys._Pop_ups_Btn_NwGm1);
        let newGameButtonBounds = this.newGameButton.getBounds();
        this.newGameButton.input.hitArea = new Phaser.Geom.Rectangle(newGameButtonBounds.x + (newGameButtonBounds.width / 2), newGameButtonBounds.y + (newGameButtonBounds.height / 2),
            newGameButtonBounds.width, newGameButtonBounds.height);
        this.newGameButton.setPosition(297, 450);
        this.newGameButton.on(CubeButtonEvents.CLICKED, () => 
        {
            // show new game confirmation
            ABCya.Tweens.addTween(this.newGameConfirmation, 500, { alpha: 1, delay: 250 }, Phaser.Math.Easing.Quadratic.Out, () =>
            {
                this.yesButton.setInteractive();
                this.noButton.setInteractive();
            });
            ABCya.Tweens.addTween(this.newGameContinue, 500, { alpha: 0 }, Phaser.Math.Easing.Quadratic.In);

            this.continueButton.disableInteractive();
            this.newGameButton.disableInteractive();
        });

        this.continueButton = new CubeButton(this.scene, ImageKeys._Pop_ups_Btn_Cont3, ImageKeys._Pop_ups_Btn_Cont2, ImageKeys._Pop_ups_Btn_Cont1);
        let continueButtonBounds = this.continueButton.getBounds();
        this.continueButton.input.hitArea = new Phaser.Geom.Rectangle(continueButtonBounds.x + (continueButtonBounds.width / 2), continueButtonBounds.y + (continueButtonBounds.height / 2),
            continueButtonBounds.width, continueButtonBounds.height);
        this.continueButton.setPosition(727, 450);
        this.continueButton.on(CubeButtonEvents.CLICKED, () => 
        {
            this.scene.events.emit(GameEvents.GO_TO_STATE, GameStates.SHAPE_REVIEW);
        });

        this.newGameContinue.add([this.newGameButton, this.continueButton]);
    }
}